import { useState, useEffect, useCallback } from 'react';
import { api } from '@/App';
import { useTheme } from '@/contexts/ThemeContext';
import { toast } from 'sonner';
import ReactFlow, {
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  Handle,
  Position,
  MarkerType,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { 
  Shield, Server, Database, AlertTriangle, Cpu, Globe, Lock, Zap, RefreshCw,
  Play, ArrowRight, Clock, Target, Eye, Bug, User, Network, ChevronRight
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

// Timeline Attack Path Node
const TimelineNode = ({ data, selected }) => {
  const { isDarkMode, colors, Icon, stage } = data;
  
  const stageColors = {
    reconnaissance: { bg: isDarkMode ? 'rgba(99, 102, 241, 0.15)' : 'rgba(99, 102, 241, 0.1)', border: '#6366f1', icon: Eye },
    'initial-access': { bg: isDarkMode ? 'rgba(249, 115, 22, 0.15)' : 'rgba(249, 115, 22, 0.1)', border: '#f97316', icon: Globe },
    execution: { bg: isDarkMode ? 'rgba(239, 68, 68, 0.15)' : 'rgba(239, 68, 68, 0.1)', border: '#ef4444', icon: Zap },
    'privilege-escalation': { bg: isDarkMode ? 'rgba(168, 85, 247, 0.15)' : 'rgba(168, 85, 247, 0.1)', border: '#a855f7', icon: Lock },
    'lateral-movement': { bg: isDarkMode ? 'rgba(236, 72, 153, 0.15)' : 'rgba(236, 72, 153, 0.1)', border: '#ec4899', icon: Network },
    exfiltration: { bg: isDarkMode ? 'rgba(220, 38, 38, 0.15)' : 'rgba(220, 38, 38, 0.1)', border: '#dc2626', icon: Database },
    impact: { bg: isDarkMode ? 'rgba(127, 29, 29, 0.15)' : 'rgba(127, 29, 29, 0.1)', border: '#7f1d1d', icon: Target },
  };
  
  const stageStyle = stageColors[stage] || stageColors.reconnaissance;
  const StageIcon = stageStyle.icon;
  
  return (
    <div
      className={`relative transition-all duration-300 ${selected ? 'scale-105' : ''}`}
      style={{
        background: stageStyle.bg,
        border: `2px solid ${stageStyle.border}`,
        borderRadius: '12px',
        padding: '16px',
        minWidth: '200px',
        boxShadow: selected 
          ? `0 0 20px ${stageStyle.border}60, 0 0 40px ${stageStyle.border}30` 
          : isDarkMode ? '0 4px 20px rgba(0,0,0,0.3)' : '0 4px 20px rgba(0,0,0,0.1)',
      }}
    >
      <Handle 
        type="target" 
        position={Position.Left} 
        style={{ background: stageStyle.border, width: 10, height: 10 }} 
      />
      
      <div className="flex items-center gap-3 mb-3">
        <div 
          className="w-10 h-10 rounded-lg flex items-center justify-center"
          style={{ background: `${stageStyle.border}30` }}
        >
          <StageIcon className="w-5 h-5" style={{ color: stageStyle.border }} />
        </div>
        <div>
          <span 
            className="font-inter font-bold text-sm block"
            style={{ color: isDarkMode ? '#fff' : '#1e293b' }}
          >
            {data.label}
          </span>
          <span 
            className="text-xs uppercase font-mono"
            style={{ color: stageStyle.border }}
          >
            {stage?.replace('-', ' ')}
          </span>
        </div>
      </div>
      
      {data.technique && (
        <div className={`text-xs px-2 py-1 rounded mb-2 ${isDarkMode ? 'bg-white/5' : 'bg-slate-100'}`}>
          <span className={isDarkMode ? 'text-zinc-400' : 'text-slate-500'}>MITRE: </span>
          <span className={isDarkMode ? 'text-white' : 'text-slate-900'}>{data.technique}</span>
        </div>
      )}
      
      {data.description && (
        <p className={`text-xs leading-relaxed ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>
          {data.description}
        </p>
      )}
      
      {data.vulns && data.vulns.length > 0 && (
        <div className="mt-3 pt-3 border-t" style={{ borderColor: `${stageStyle.border}30` }}>
          <div className="flex items-center gap-1 text-xs mb-1">
            <Bug className="w-3 h-3 text-red-500" />
            <span className="text-red-500 font-medium">{data.vulns.length} vulnerabilities</span>
          </div>
        </div>
      )}
      
      <Handle 
        type="source" 
        position={Position.Right} 
        style={{ background: stageStyle.border, width: 10, height: 10 }} 
      />
    </div>
  );
};

const nodeTypes = { timeline: TimelineNode };

export default function AttackPathVisualization() {
  const { isDarkMode } = useTheme();
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [paths, setPaths] = useState([]);
  const [selectedPath, setSelectedPath] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [view, setView] = useState('timeline');

  useEffect(() => {
    fetchAttackPaths();
  }, []);

  const fetchAttackPaths = async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    try {
      const response = await api.get('/attack-paths');
      // Backend returns {nodes, edges, ...} object, not an array of paths
      // Check if response.data is an array, otherwise use sample paths
      const pathData = Array.isArray(response.data) ? response.data : [];
      
      if (pathData.length === 0) {
        // Generate sample attack paths for demonstration
        const samplePaths = generateSamplePaths();
        setPaths(samplePaths);
        if (samplePaths.length > 0) {
          setSelectedPath(samplePaths[0]);
          generateFlowFromPath(samplePaths[0]);
        }
      } else {
        setPaths(pathData);
        if (pathData.length > 0) {
          setSelectedPath(pathData[0]);
          generateFlowFromPath(pathData[0]);
        }
      }
      if (isRefresh) toast.success('Attack paths refreshed');
    } catch (error) {
      console.error('Failed to fetch attack paths:', error);
      const samplePaths = generateSamplePaths();
      setPaths(samplePaths);
      if (samplePaths.length > 0) {
        setSelectedPath(samplePaths[0]);
        generateFlowFromPath(samplePaths[0]);
      }
      if (isRefresh) toast.error('Failed to refresh attack paths');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const generateSamplePaths = () => {
    return [
      {
        id: 'path-1',
        name: 'External Web Server to Database',
        severity: 'critical',
        likelihood: 85,
        stages: [
          { id: 'recon-1', stage: 'reconnaissance', label: 'Port Scanning', technique: 'T1046', description: 'Attacker scans external-facing services' },
          { id: 'access-1', stage: 'initial-access', label: 'SQL Injection', technique: 'T1190', description: 'Exploit unpatched web application vulnerability', vulns: ['CVE-2024-1234'] },
          { id: 'exec-1', stage: 'execution', label: 'Web Shell Upload', technique: 'T1505.003', description: 'Deploy persistent backdoor on web server' },
          { id: 'priv-1', stage: 'privilege-escalation', label: 'Kernel Exploit', technique: 'T1068', description: 'Escalate to root privileges' },
          { id: 'lateral-1', stage: 'lateral-movement', label: 'SSH Key Theft', technique: 'T1021.004', description: 'Move to internal database server' },
          { id: 'exfil-1', stage: 'exfiltration', label: 'Data Theft', technique: 'T1048', description: 'Extract sensitive customer data' },
        ]
      },
      {
        id: 'path-2',
        name: 'Phishing to Crown Jewel Access',
        severity: 'high',
        likelihood: 65,
        stages: [
          { id: 'recon-2', stage: 'reconnaissance', label: 'OSINT Gathering', technique: 'T1589', description: 'Collect employee information from LinkedIn' },
          { id: 'access-2', stage: 'initial-access', label: 'Spear Phishing', technique: 'T1566.001', description: 'Send targeted phishing email to finance team' },
          { id: 'exec-2', stage: 'execution', label: 'Macro Payload', technique: 'T1204.002', description: 'User enables malicious macro' },
          { id: 'priv-2', stage: 'privilege-escalation', label: 'Token Theft', technique: 'T1134', description: 'Steal admin credentials from memory' },
          { id: 'impact-2', stage: 'impact', label: 'Ransomware', technique: 'T1486', description: 'Encrypt critical business files' },
        ]
      },
      {
        id: 'path-3',
        name: 'Supply Chain Compromise',
        severity: 'high',
        likelihood: 45,
        stages: [
          { id: 'recon-3', stage: 'reconnaissance', label: 'Vendor Research', technique: 'T1591', description: 'Identify software dependencies' },
          { id: 'access-3', stage: 'initial-access', label: 'Dependency Confusion', technique: 'T1195.002', description: 'Inject malicious package' },
          { id: 'exec-3', stage: 'execution', label: 'Code Execution', technique: 'T1059', description: 'Execute during build process' },
          { id: 'lateral-3', stage: 'lateral-movement', label: 'CI/CD Pivot', technique: 'T1072', description: 'Access production deployment pipeline' },
          { id: 'exfil-3', stage: 'exfiltration', label: 'Secret Theft', technique: 'T1552', description: 'Extract API keys and credentials' },
        ]
      }
    ];
  };

  const generateFlowFromPath = (path) => {
    if (!path || !path.stages) return;
    
    const newNodes = path.stages.map((stage, index) => ({
      id: stage.id,
      type: 'timeline',
      position: { x: index * 280, y: 150 },
      data: { 
        ...stage, 
        isDarkMode,
      },
    }));

    const newEdges = path.stages.slice(0, -1).map((stage, index) => ({
      id: `edge-${index}`,
      source: stage.id,
      target: path.stages[index + 1].id,
      type: 'smoothstep',
      animated: true,
      style: { 
        stroke: isDarkMode ? '#6366f1' : '#4f46e5', 
        strokeWidth: 2,
      },
      markerEnd: {
        type: MarkerType.ArrowClosed,
        color: isDarkMode ? '#6366f1' : '#4f46e5',
      },
    }));

    setNodes(newNodes);
    setEdges(newEdges);
  };

  const handlePathSelect = (path) => {
    setSelectedPath(path);
    generateFlowFromPath(path);
  };

  const getSeverityColor = (severity) => {
    const colors = {
      critical: isDarkMode ? 'bg-red-500/20 text-red-400 border-red-500/30' : 'bg-red-100 text-red-700 border-red-200',
      high: isDarkMode ? 'bg-orange-500/20 text-orange-400 border-orange-500/30' : 'bg-orange-100 text-orange-700 border-orange-200',
      medium: isDarkMode ? 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' : 'bg-yellow-100 text-yellow-700 border-yellow-200',
      low: isDarkMode ? 'bg-green-500/20 text-green-400 border-green-500/30' : 'bg-green-100 text-green-700 border-green-200',
    };
    return colors[severity] || colors.medium;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className={`font-inter text-xl ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`}>Loading attack paths...</div>
      </div>
    );
  }

  return (
    <div data-testid="attack-path-container" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className={`font-inter font-bold text-3xl ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
            Attack Path Analysis
          </h1>
          <p className={`font-inter mt-1 ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>
            Timeline-based threat path visualization using MITRE ATT&CK framework
          </p>
        </div>
        <Button 
          onClick={() => fetchAttackPaths(true)}
          disabled={refreshing}
          className="bg-indigo-600 hover:bg-indigo-500"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          {refreshing ? 'Refreshing...' : 'Refresh Paths'}
        </Button>
      </div>

      {/* Attack Path List */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {paths.map((path) => (
          <div
            key={path.id}
            data-testid={`attack-path-${path.id}`}
            onClick={() => handlePathSelect(path)}
            className={`rounded-xl p-5 cursor-pointer transition-all border-2 ${
              selectedPath?.id === path.id 
                ? isDarkMode 
                  ? 'border-indigo-500 bg-indigo-500/10' 
                  : 'border-indigo-500 bg-indigo-50'
                : isDarkMode 
                  ? 'border-transparent glass hover:border-white/20' 
                  : 'border-slate-200 bg-white hover:border-slate-300 shadow-card'
            }`}
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isDarkMode ? 'bg-indigo-500/20' : 'bg-indigo-100'}`}>
                  <Target className={`w-5 h-5 ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`} />
                </div>
                <div>
                  <h3 className={`font-inter font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{path.name}</h3>
                  <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>{path.stages?.length || 0} stages</p>
                </div>
              </div>
              <span className={`px-2 py-1 rounded text-xs uppercase font-medium border ${getSeverityColor(path.severity)}`}>
                {path.severity}
              </span>
            </div>
            
            {/* Mini Timeline Preview */}
            <div className="flex items-center gap-1 mt-4">
              {path.stages?.slice(0, 5).map((stage, idx) => (
                <div key={stage.id} className="flex items-center">
                  <div 
                    className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                      stage.stage === 'reconnaissance' ? 'bg-indigo-500/30 text-indigo-400' :
                      stage.stage === 'initial-access' ? 'bg-orange-500/30 text-orange-400' :
                      stage.stage === 'execution' ? 'bg-red-500/30 text-red-400' :
                      stage.stage === 'privilege-escalation' ? 'bg-purple-500/30 text-purple-400' :
                      stage.stage === 'lateral-movement' ? 'bg-pink-500/30 text-pink-400' :
                      stage.stage === 'exfiltration' ? 'bg-rose-500/30 text-rose-400' :
                      'bg-zinc-500/30 text-zinc-400'
                    }`}
                  >
                    {idx + 1}
                  </div>
                  {idx < Math.min(path.stages.length - 1, 4) && (
                    <ChevronRight className={`w-3 h-3 ${isDarkMode ? 'text-zinc-600' : 'text-slate-400'}`} />
                  )}
                </div>
              ))}
              {path.stages?.length > 5 && (
                <span className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>+{path.stages.length - 5}</span>
              )}
            </div>
            
            <div className={`mt-3 pt-3 border-t flex items-center justify-between ${isDarkMode ? 'border-white/10' : 'border-slate-200'}`}>
              <span className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Likelihood</span>
              <div className="flex items-center gap-2">
                <div className={`w-16 h-2 rounded-full overflow-hidden ${isDarkMode ? 'bg-zinc-800' : 'bg-slate-200'}`}>
                  <div 
                    className={`h-full rounded-full ${
                      path.likelihood >= 70 ? 'bg-red-500' : path.likelihood >= 40 ? 'bg-yellow-500' : 'bg-green-500'
                    }`}
                    style={{ width: `${path.likelihood}%` }}
                  />
                </div>
                <span className={`text-xs font-mono ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{path.likelihood}%</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Flow Visualization */}
      {selectedPath && (
        <div className={`rounded-2xl overflow-hidden ${isDarkMode ? 'glass' : 'bg-white border border-slate-200 shadow-card'}`}>
          <div className={`px-6 py-4 border-b flex items-center justify-between ${isDarkMode ? 'border-white/10' : 'border-slate-200'}`}>
            <div>
              <h2 className={`font-inter font-semibold text-lg ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                {selectedPath.name}
              </h2>
              <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                Click and drag to explore • Scroll to zoom
              </p>
            </div>
            <div className="flex items-center gap-4">
              <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg ${isDarkMode ? 'bg-zinc-800' : 'bg-slate-100'}`}>
                <Clock className={`w-4 h-4 ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`} />
                <span className={`text-xs ${isDarkMode ? 'text-zinc-300' : 'text-slate-700'}`}>
                  {selectedPath.stages?.length} Attack Stages
                </span>
              </div>
            </div>
          </div>
          
          <div style={{ height: '500px' }}>
            <ReactFlow
              nodes={nodes}
              edges={edges}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              nodeTypes={nodeTypes}
              fitView
              attributionPosition="bottom-left"
              style={{ background: isDarkMode ? '#0a0a0f' : '#f8fafc' }}
            >
              <Controls 
                style={{ 
                  background: isDarkMode ? '#18181b' : '#fff',
                  border: isDarkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0',
                  borderRadius: '8px'
                }}
              />
              <MiniMap 
                style={{ 
                  background: isDarkMode ? '#18181b' : '#fff',
                  border: isDarkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0'
                }}
                nodeColor={(node) => {
                  const stageColors = {
                    reconnaissance: '#6366f1',
                    'initial-access': '#f97316',
                    execution: '#ef4444',
                    'privilege-escalation': '#a855f7',
                    'lateral-movement': '#ec4899',
                    exfiltration: '#dc2626',
                    impact: '#7f1d1d',
                  };
                  return stageColors[node.data?.stage] || '#6366f1';
                }}
              />
              <Background 
                color={isDarkMode ? '#27272a' : '#e2e8f0'} 
                gap={20} 
                size={1}
              />
            </ReactFlow>
          </div>
        </div>
      )}

      {/* Stage Legend */}
      <div className={`rounded-xl p-4 ${isDarkMode ? 'glass' : 'bg-white border border-slate-200 shadow-card'}`}>
        <h3 className={`font-inter font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>MITRE ATT&CK Stages</h3>
        <div className="flex flex-wrap gap-3">
          {[
            { stage: 'reconnaissance', label: 'Reconnaissance', color: 'indigo' },
            { stage: 'initial-access', label: 'Initial Access', color: 'orange' },
            { stage: 'execution', label: 'Execution', color: 'red' },
            { stage: 'privilege-escalation', label: 'Privilege Escalation', color: 'purple' },
            { stage: 'lateral-movement', label: 'Lateral Movement', color: 'pink' },
            { stage: 'exfiltration', label: 'Exfiltration', color: 'rose' },
            { stage: 'impact', label: 'Impact', color: 'red' },
          ].map(item => (
            <div 
              key={item.stage}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg ${isDarkMode ? `bg-${item.color}-500/10` : `bg-${item.color}-50`}`}
            >
              <div className={`w-3 h-3 rounded-full bg-${item.color}-500`} />
              <span className={`text-xs ${isDarkMode ? `text-${item.color}-400` : `text-${item.color}-700`}`}>{item.label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
